import { useCallback } from 'react'

const MAX_DIMENSION = 800
const TARGET_BYTES = 150 * 1024

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Image load failed'))
    }
    img.src = url
  })
}

function drawResized(img: HTMLImageElement): HTMLCanvasElement {
  let w = img.naturalWidth
  let h = img.naturalHeight

  if (w > MAX_DIMENSION || h > MAX_DIMENSION) {
    if (w >= h) {
      h = Math.round((h * MAX_DIMENSION) / w)
      w = MAX_DIMENSION
    } else {
      w = Math.round((w * MAX_DIMENSION) / h)
      h = MAX_DIMENSION
    }
  }

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  canvas.getContext('2d')!.drawImage(img, 0, 0, w, h)
  return canvas
}

function canvasToFile(
  canvas: HTMLCanvasElement,
  name: string,
  quality: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Canvas export failed'))
          return
        }
        resolve(
          new File([blob], name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }),
        )
      },
      'image/jpeg',
      quality,
    )
  })
}

async function compressToTarget(canvas: HTMLCanvasElement, name: string): Promise<File> {
  let quality = 0.85
  let file = await canvasToFile(canvas, name, quality)
  while (file.size > TARGET_BYTES && quality >= 0.3) {
    quality = Math.round((quality - 0.1) * 10) / 10
    file = await canvasToFile(canvas, name, quality)
  }
  // Returns best-effort result if still above target at minimum quality
  return file
}

export async function optimizeImage(file: File): Promise<File> {
  const img = await loadImage(file)

  const fitsWithinBounds =
    file.size <= TARGET_BYTES &&
    img.naturalWidth <= MAX_DIMENSION &&
    img.naturalHeight <= MAX_DIMENSION

  if (fitsWithinBounds) return file

  const canvas = drawResized(img)
  return compressToTarget(canvas, file.name)
}

export function useImageOptimizer() {
  const optimize = useCallback((file: File) => optimizeImage(file), [])
  return { optimize }
}
